import { useEffect, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [quote, setQuote] = useState("");
  const [author, setAuthor] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({});

  const authHeaders = () => {
    const token = localStorage.getItem("nox_admin_token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/testimonials`);
      setItems(await res.json());
      setLoaded(true);
    } catch {
      setError("Could not load the testimonials.");
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    if (!quote.trim() || !author.trim()) {
      setError("A quote and an author are both needed.");
      return;
    }
    setBusy("add");
    try {
      const res = await fetch(`${API}/api/admin/testimonials`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ quote, author, source }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Add failed");
      setQuote("");
      setAuthor("");
      setSource("");
      await load();
    } catch (err) {
      setError(err.message || "Add failed.");
    } finally {
      setBusy("");
    }
  };

  const saveEdit = async (id) => {
    setError("");
    const e = edits[id];
    if (!e || !e.quote?.trim() || !e.author?.trim()) {
      setError("Quote and author cannot be empty.");
      return;
    }
    setBusy(id);
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify(e),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Save failed");
      setEdits({ ...edits, [id]: undefined });
      await load();
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setBusy("");
    }
  };

  const act = async (endpoint, body) => {
    setError("");
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Action failed");
      await load();
    } catch (err) {
      setError(err.message || "Action failed.");
    }
  };

  const del = async (id) => {
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed");
      await load();
    } catch (err) {
      setError(err.message || "Delete failed.");
    }
  };

  return (
    <div className="mt-10" data-testid="admin-testimonials">
      <p className="max-w-2xl text-xs leading-relaxed text-slate-500">
        Real words from real DMs, streamers and developers. Approved quotes appear on the Commission
        page — pull them from your YouTube comments, patron messages or email. Never invented.
      </p>

      <form onSubmit={add} className="mt-6 border border-gold/30 bg-[#0d0d12] p-6" data-testid="testimonials-add">
        <h3 className="font-display text-sm tracking-[0.2em] text-silver">Add a testimonial</h3>
        <div className="mt-5 space-y-4">
          <textarea
            className="input-night min-h-[88px] resize-y"
            placeholder="The quote — e.g. When the Strahd motif hit, the table went silent…"
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            data-testid="testimonials-add-quote"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              className="input-night"
              placeholder="Author — e.g. DM Marcus K."
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              data-testid="testimonials-add-author"
            />
            <input
              className="input-night"
              placeholder="Source (optional) — e.g. YouTube comment"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="testimonials-add-source"
            />
          </div>
        </div>
        {error && (
          <p className="mt-3 text-xs text-red-500" data-testid="testimonials-error">
            {error}
          </p>
        )}
        <button className="btn-night mt-5" type="submit" disabled={busy === "add"} data-testid="testimonials-add-btn">
          {busy === "add" ? "Adding…" : "Add Testimonial"}
        </button>
      </form>

      <div className="mt-8 space-y-3">
        {!loaded && (
          <div className="flex items-center justify-center gap-4 py-10">
            <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
            <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
          </div>
        )}
        {loaded && items.length === 0 && (
          <p className="border border-line bg-surface p-6 text-center font-cormorant text-lg italic text-slate-500">
            No testimonials yet — the Commission page hides the section until the first real quote arrives.
          </p>
        )}
        {items.map((t, i) => {
          const e = edits[t.id];
          return (
            <div key={t.id} className="border border-line bg-surface p-5" data-testid={`testimonial-row-${i}`}>
              <textarea
                className="input-night min-h-[72px] resize-y text-sm"
                value={e ? e.quote : t.quote}
                onChange={(ev) => setEdits({ ...edits, [t.id]: { ...(e || { author: e ? e.author : t.author, source: e ? e.source : t.source }), quote: ev.target.value } })}
                data-testid={`testimonial-quote-input-${i}`}
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  className="input-night min-w-0 flex-1 !py-2 text-sm"
                  value={e ? e.author : t.author}
                  onChange={(ev) => setEdits({ ...edits, [t.id]: { quote: e ? e.quote : t.quote, source: e ? e.source : t.source, author: ev.target.value } })}
                  data-testid={`testimonial-author-input-${i}`}
                />
                <input
                  className="input-night min-w-0 flex-1 !py-2 text-sm"
                  placeholder="Source (optional)"
                  value={(e ? e.source : t.source) || ""}
                  onChange={(ev) => setEdits({ ...edits, [t.id]: { quote: e ? e.quote : t.quote, author: e ? e.author : t.author, source: ev.target.value } })}
                  data-testid={`testimonial-source-input-${i}`}
                />
                <button
                  className="btn-ghost-night cursor-pointer !px-3 !py-1.5 !text-[9px]"
                  onClick={() => saveEdit(t.id)}
                  disabled={busy !== ""}
                  data-testid={`testimonial-save-${i}`}
                >
                  Save
                </button>
                <div className="flex gap-2">
                  <button
                    className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                    onClick={() => act(`/api/admin/testimonials/${t.id}/move`, { direction: "up" })}
                    disabled={i === 0 || busy !== ""}
                    aria-label={`Move testimonial ${i + 1} up`}
                    data-testid={`testimonial-up-${i}`}
                  >
                    ▲
                  </button>
                  <button
                    className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                    onClick={() => act(`/api/admin/testimonials/${t.id}/move`, { direction: "down" })}
                    disabled={i === items.length - 1 || busy !== ""}
                    aria-label={`Move testimonial ${i + 1} down`}
                    data-testid={`testimonial-down-${i}`}
                  >
                    ▼
                  </button>
                </div>
                <button
                  className="cursor-pointer border border-red-900/60 px-3 py-1.5 font-jmono text-[9px] uppercase tracking-[0.25em] text-red-400 transition-colors hover:bg-red-900/20"
                  onClick={() => del(t.id)}
                  disabled={busy !== ""}
                  data-testid={`testimonial-del-${i}`}
                >
                  Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTestimonials;
